import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  InputAdornment,
  List,
  ListItemButton,
  IconButton,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { theme } from "../../theme/colors";
import { POLICY_NAV_LABEL } from "../../constants/policyLabels";
import { fetchBills } from "../../utils/utils";

function formatBillLabel(bill) {
  const prefix = bill.type ? bill.type.toUpperCase() : "";
  return prefix ? `${prefix} ${bill.bill_number}` : (bill.bill_number || "");
}

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 350;

export default function BillSelector({ selectedBill, onBillSelect, onClearBill, label = "Select Bill", error }) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // Token-based race-condition guard: only the latest request gets to write to state.
  const requestTokenRef = useRef(0);
  const debounceTimerRef = useRef(null);

  const runFetch = useCallback(async (pageNum, query, reset) => {
    const myToken = ++requestTokenRef.current;
    if (reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    try {
      const response = await fetchBills(pageNum, PAGE_SIZE, query, [], "", "", "");
      if (myToken !== requestTokenRef.current) return; // stale response, ignore
      const newBills = response.bills || response.results || [];
      if (reset) {
        setBills(newBills);
      } else {
        setBills((prev) => [...prev, ...newBills]);
      }
      setHasMore(Array.isArray(newBills) && newBills.length === PAGE_SIZE);
      setPage(pageNum);
    } catch (err) {
      if (myToken !== requestTokenRef.current) return;
      console.error("Error loading bills:", err);
      if (reset) setBills([]);
    } finally {
      if (myToken === requestTokenRef.current) {
        if (reset) setLoading(false);
        else setLoadingMore(false);
      }
    }
  }, []);

  useEffect(() => {
    if (open) {
      setSearchQuery("");
      runFetch(1, "", true);
    } else {
      requestTokenRef.current++;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    }
  }, [open, runFetch]);

  useEffect(() => () => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
  }, []);

  const handleSearchChange = (e) => {
    const q = e.target.value;
    setSearchQuery(q);
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      runFetch(1, q, true);
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    runFetch(1, searchQuery, true);
  };

  const handleClearSearch = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    setSearchQuery("");
    runFetch(1, "", true);
  };

  const handleLoadMore = () => {
    if (loading || loadingMore || !hasMore) return;
    runFetch(page + 1, searchQuery, false);
  };

  const handleBillSelect = (bill) => {
    onBillSelect(bill);
    setOpen(false);
  };

  return (
    <Box>
      <Typography sx={{ fontSize: 16, fontWeight: 600, color: "#0f172a", mb: 1 }}>
        {label}
      </Typography>
      <Box
        onClick={() => setOpen(true)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "#fff",
          border: "2px solid",
          borderColor: error && !selectedBill ? "#ef4444" : "#e5e7eb",
          borderRadius: "12px",
          p: 2,
          cursor: "pointer",
          "&:hover": { borderColor: "#d1d5db" },
        }}
      >
        {selectedBill ? (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flex: 1, minWidth: 0 }}>
              <DescriptionOutlinedIcon sx={{ fontSize: 20, color: theme.primary }} />
              <Typography sx={{ fontSize: 15, fontWeight: 600, color: theme.primary }} noWrap>
                {formatBillLabel(selectedBill)} - {(selectedBill.title || "").substring(0, 40)}...
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                if (onClearBill) onClearBill();
                else onBillSelect(null);
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        ) : (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flex: 1 }}>
              <DescriptionOutlinedIcon sx={{ fontSize: 20, color: "#64748b" }} />
              <Typography sx={{ fontSize: 15, color: "#64748b" }}>
                Search and select a bill
              </Typography>
            </Box>
            <ExpandMoreIcon sx={{ fontSize: 22, color: "#475569" }} />
          </>
        )}
      </Box>
      {error && !selectedBill && (
        <Typography sx={{ fontSize: 13, color: "#ef4444", mt: 1, pl: 0.5 }}>{error}</Typography>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "1.125rem" }}>Select Bill</Typography>
          <IconButton onClick={() => setOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSearchSubmit} sx={{ mb: 2 }}>
            <TextField
              fullWidth
              autoFocus
              placeholder='Try "HR 1234", "climate", or a sponsor name'
              value={searchQuery}
              onChange={handleSearchChange}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconButton
                      type="submit"
                      size="small"
                      aria-label={`Search ${POLICY_NAV_LABEL.toLowerCase()}`}
                      edge="start"
                      sx={{ color: "#475569", "&:hover": { color: theme.primary } }}
                    >
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
                endAdornment: loading && bills.length > 0 ? (
                  // Inline spinner while a background search is in flight, so
                  // the list below stays visible instead of "collapsing" to a
                  // big spinner on every keystroke.
                  <InputAdornment position="end">
                    <CircularProgress size={18} sx={{ color: theme.primary }} />
                  </InputAdornment>
                ) : searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleClearSearch}
                      size="small"
                      aria-label="Clear search"
                      edge="end"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "#f9fafb" } }}
            />
          </Box>
          {loading && bills.length === 0 ? (
            // Only show the big centered spinner on the initial load. Once we
            // have any results, subsequent searches use the inline spinner so
            // the dialog doesn't visibly collapse between keystrokes.
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress sx={{ color: theme.primary }} />
            </Box>
          ) : !loading && bills.length === 0 ? (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <Typography sx={{ fontSize: 14, color: "#64748b" }}>
                {searchQuery
                  ? `No bills found for "${searchQuery}".`
                  : "No bills available."}
              </Typography>
            </Box>
          ) : (
            <List disablePadding sx={{ maxHeight: 360, overflow: "auto" }}>
              {bills.map((item) => (
                <ListItemButton
                  key={item.id}
                  onClick={() => handleBillSelect(item)}
                  sx={{
                    borderRadius: "8px",
                    mb: 0.5,
                    "&:hover": { bgcolor: `${theme.primary}08` },
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: theme.primary, mb: 0.5 }}>
                      {formatBillLabel(item)}
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: "#334155", mb: 0.25 }} noWrap>
                      {item.title}
                    </Typography>
                    {item.sponsor_name && (
                      <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                        Sponsored by {item.sponsor_name}
                      </Typography>
                    )}
                  </Box>
                  {selectedBill?.id === item.id && (
                    <CheckCircleIcon sx={{ color: theme.primary, ml: 1 }} />
                  )}
                </ListItemButton>
              ))}
            </List>
          )}
          {!loading && bills.length > 0 && hasMore && (
            <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}>
              <Typography
                component="button"
                type="button"
                onClick={handleLoadMore}
                disabled={loadingMore}
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: theme.primary,
                  cursor: loadingMore ? "default" : "pointer",
                  border: "none",
                  background: "none",
                  opacity: loadingMore ? 0.6 : 1,
                }}
              >
                {loadingMore ? "Loading…" : "Load more"}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
